import type { BannerStateInfo } from './BannerStateContainer.ts';
import { findBanner } from './BannerStateContainer.ts';
import { BaseStateContainer } from './BaseStateContainer.ts';

type FloatingBannerStateContainerState = {
	floatingBanners: BannerStateInfo[];
};

export class FloatingBannerStateContainer extends BaseStateContainer<FloatingBannerStateContainerState> {
	state: FloatingBannerStateContainerState = {
		floatingBanners: [],
	};

	showFloating = (name: string, height: number): void => {
		// The lookup has to happen inside the updater: updates queued in the same microtask are
		// applied in sequence, so an index resolved beforehand would already be stale.
		void this.setState((state) => {
			const index = findBanner(name, state.floatingBanners);

			if (index === -1) {
				return { ...state, floatingBanners: [...state.floatingBanners, { name, height }] };
			}

			if (state.floatingBanners[index].height !== height) {
				const newArray = [...state.floatingBanners];
				// Replace rather than mutate, so the previous state keeps the old height.
				newArray[index] = { name, height };
				return { ...state, floatingBanners: newArray };
			}

			return undefined;
		});
	};

	hideFloating = (name: string): void => {
		void this.setState((state) => {
			const index = findBanner(name, state.floatingBanners);

			if (index > -1) {
				const newArray = [...state.floatingBanners];
				newArray.splice(index, 1);
				return { ...state, floatingBanners: newArray };
			}

			return undefined;
		});
	};

	getFloatingHeightBefore = (name: string): number => {
		const index = findBanner(name, this.state.floatingBanners);
		if (index === -1) {
			return 0;
		}

		return this.state.floatingBanners
			.slice(0, index)
			.reduce((height: number, banner: BannerStateInfo) => {
				return height + banner.height;
			}, 0);
	};

	getFloatingHeight = (): number =>
		this.state.floatingBanners.reduce(
			(height: number, banner: BannerStateInfo) => height + banner.height,
			0,
		);
}
